import { useEffect } from 'react';
import { FiMapPin, FiPhone, FiMail, FiCalendar, FiChevronRight, FiCheckCircle, FiClock } from 'react-icons/fi';
import { GiSparkles } from 'react-icons/gi';
import { FaWhatsapp, FaInstagram, FaFacebookF, FaLinkedinIn } from 'react-icons/fa';

const Contact = () => {
  const contactInfo = [
    {
      icon: FiMapPin,
      title: 'Head Office',
      details: ['Champa Dubey Bhawan, Civil Lines', 'Prayagraj, Uttar Pradesh - 211001'],
      action: { text: 'Get Directions', link: '#' },
    },
    {
      icon: FiMapPin,
      title: 'Lucknow Office',
      details: ['Omaxe Hazratganj', 'Near Police Headquarters & Ekana Stadium', 'Lucknow, Uttar Pradesh'],
      action: { text: 'Get Directions', link: '#' },
    },
    {
      icon: FiPhone,
      title: 'Contact Us',
      details: ['+91 9336525214'],
      action: { text: 'Call Now', link: 'tel:+919336525214' },
    },
    {
      icon: FiMail,
      title: 'Email Us',
      details: ['purvanchalglobalventurespvtltd@gmail.com'],
      action: { text: 'Send Email', link: 'mailto:purvanchalglobalventurespvtltd@gmail.com' },
    },
  ];

  const socialLinks = [
    { icon: FaInstagram, href: '#', label: 'Instagram' },
    { icon: FaFacebookF, href: '#', label: 'Facebook' },
    { icon: FaLinkedinIn, href: '#', label: 'LinkedIn' },
    { icon: FaWhatsapp, href: '#', label: 'WhatsApp' },
  ];

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes contactFadeUp {
        from { opacity: 0; transform: translateY(30px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes contactSlideLeft {
        from { opacity: 0; transform: translateX(-40px); }
        to { opacity: 1; transform: translateX(0); }
      }
      @keyframes contactSlideRight {
        from { opacity: 0; transform: translateX(40px); }
        to { opacity: 1; transform: translateX(0); }
      }
      @keyframes contactFloat {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        50% { transform: translateY(-15px) rotate(3deg); }
      }
      @keyframes contactPulse {
        0%, 100% { opacity: 0.15; transform: scale(1); }
        50% { opacity: 0.3; transform: scale(1.05); }
      }
      @keyframes contactBorderPulse {
        0%, 100% { border-color: rgba(212, 175, 55, 0.2); }
        50% { border-color: rgba(212, 175, 55, 0.6); }
      }
      .animate-contact-fade-up { animation: contactFadeUp 0.8s ease-out forwards; }
      .animate-contact-slide-left { animation: contactSlideLeft 0.8s ease-out forwards; }
      .animate-contact-slide-right { animation: contactSlideRight 0.8s ease-out forwards; }
      .animate-contact-float { animation: contactFloat linear infinite; }
      .animate-contact-pulse { animation: contactPulse 4s ease-in-out infinite; }
      .delay-100 { animation-delay: 0.1s; }
      .delay-200 { animation-delay: 0.2s; }
      .delay-300 { animation-delay: 0.3s; }
      .font-serif { font-family: 'Cormorant Garamond', serif; }
      .font-montserrat { font-family: 'Montserrat', sans-serif; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <section id="contact" className="relative py-16 sm:py-20 md:py-28 lg:py-32 overflow-hidden bg-navy">
      {/* Ambient Background - Matching Hero Section */}
      <div className="absolute inset-0">
        {/* Dark base gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-navy via-navy/95 to-navy"></div>
        
        {/* Golden orb glow - top right */}
        <div className="absolute top-0 right-0 w-[20rem] h-[20rem] sm:w-[30rem] sm:h-[30rem] md:w-[40rem] md:h-[40rem] lg:w-[50rem] lg:h-[50rem] bg-gold/5 rounded-full blur-3xl animate-contact-pulse"></div>
        
        {/* Golden orb glow - bottom left */}
        <div className="absolute bottom-0 left-0 w-[18rem] h-[18rem] sm:w-[25rem] sm:h-[25rem] md:w-[35rem] md:h-[35rem] lg:w-[40rem] lg:h-[40rem] bg-amber-500/5 rounded-full blur-3xl animate-contact-pulse" style={{ animationDelay: '2s' }}></div>
        
        {/* Floating particles - matching hero section */}
        {[...Array(25)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-gold/20 rounded-full animate-contact-float"
            style={{
              width: `${Math.random() * 2 + 1}px`,
              height: `${Math.random() * 2 + 1}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 8}s`,
              animationDuration: `${Math.random() * 10 + 8}s`,
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-8 lg:px-12 z-10">
        {/* Section Header - Matching Hero Typography */}
        <div className="text-center mb-12 sm:mb-16 md:mb-20 animate-contact-fade-up">
          <div className="inline-flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5">
            <div className="w-6 sm:w-8 h-px bg-gold"></div>
            <span className="text-gold text-[9px] sm:text-[10px] md:text-[11px] font-semibold tracking-[0.2em] sm:tracking-[0.24em] md:tracking-[0.28em] uppercase font-montserrat">Connect With Us</span>
            <div className="w-6 sm:w-8 h-px bg-gold"></div>
          </div>
          
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light text-white mb-3 sm:mb-4">
            Begin Your Journey
            <span className="block text-gold font-normal italic mt-1 sm:mt-2">Towards Luxury Living</span>
          </h2>
          
          <div className="w-12 sm:w-16 h-px bg-gold/50 mx-auto mt-5 sm:mt-6 mb-5 sm:mb-6"></div>
          
          <p className="text-gray-300 max-w-2xl mx-auto text-xs sm:text-sm md:text-base leading-relaxed font-light px-4">
            Experience the pinnacle of luxury real estate. Our experts are here to guide you 
            through every step of your journey.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          {/* Quick Contact Banner - Matching hero eyebrow style */}
          <div className="bg-black/30 backdrop-blur-sm rounded-xl p-4 sm:p-5 border-l-2 border-gold mb-6 sm:mb-8 animate-contact-slide-left">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gold/15 flex items-center justify-center border border-gold/30 flex-shrink-0">
                  <FiCalendar size={14} className="text-gold sm:text-base" />
                </div>
                <div>
                  <p className="text-gray-400 text-[8px] sm:text-[9px] md:text-[10px] uppercase tracking-[0.15em] sm:tracking-[0.2em]">Ready to experience luxury?</p>
                  <p className="text-white font-light text-xs sm:text-sm md:text-base font-serif">Book a site visit today</p>
                </div>
              </div>
              <button className="px-4 sm:px-5 py-1.5 sm:py-2 bg-gold text-navy rounded-full text-[9px] sm:text-[10px] font-semibold tracking-[0.1em] sm:tracking-[0.12em] uppercase hover:bg-gold-dark transition-all transform hover:scale-105 flex items-center gap-1 whitespace-nowrap">
                Schedule Now
                <FiChevronRight size={10} className="sm:text-xs" />
              </button>
            </div>
          </div>

          {/* Contact Cards Grid - Matching hero stats panel style */}
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-5 md:gap-6 mb-6 sm:mb-8">
            {contactInfo.map((info, idx) => (
              <div
                key={idx}
                className="group bg-black/30 backdrop-blur-sm rounded-xl p-4 sm:p-5 md:p-6 border border-white/10 hover:border-gold/30 transition-all duration-300 hover:transform hover:-translate-y-0.5 animate-contact-slide-left"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-11 md:h-11 rounded-xl bg-gold/15 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 border border-gold/30 flex-shrink-0">
                    <info.icon size={16} className="text-gold sm:text-base md:text-lg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg md:text-xl font-light text-white mb-1 sm:mb-2 font-serif">{info.title}</h3>
                    {info.details.map((detail, i) => (
                      <p key={i} className="text-gray-300 text-xs sm:text-sm md:text-base leading-relaxed font-light break-words">
                        {detail}
                      </p>
                    ))}
                    <a
                      href={info.action.link}
                      className="inline-flex items-center gap-1 text-gold text-[9px] sm:text-[10px] md:text-xs uppercase tracking-wider mt-2 sm:mt-3 hover:gap-2 transition-all"
                    >
                      {info.action.text}
                      <FiChevronRight size={9} className="sm:text-[10px]" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Section - Social & Map & Hours */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
            {/* WhatsApp Card */}
            <div className="bg-black/30 backdrop-blur-sm rounded-xl p-4 sm:p-5 border border-white/10 hover:border-green-500/30 transition-all animate-contact-slide-right">
              <div className="flex flex-col gap-2 sm:gap-3">
                <div className="flex items-center gap-2">
                  <FaWhatsapp size={16} className="text-green-400 sm:text-lg" />
                  <span className="text-white text-[9px] sm:text-[10px] uppercase tracking-wider font-medium">WhatsApp</span>
                </div>
                <p className="text-gray-300 text-xs sm:text-sm break-words">+91 9336525214</p>
                <button className="mt-1 px-3 sm:px-4 py-1.5 sm:py-2 bg-green-500/20 text-green-400 rounded-lg text-[10px] sm:text-[11px] font-semibold hover:bg-green-500 hover:text-white transition-all w-fit">
                  Chat Now
                </button>
              </div>
            </div>

            {/* Social Links Card */}
            <div className="bg-black/30 backdrop-blur-sm rounded-xl p-4 sm:p-5 border border-white/10 transition-all animate-contact-slide-right" style={{ animationDelay: '0.1s' }}>
              <p className="text-gray-400 text-[9px] sm:text-[10px] uppercase tracking-[0.15em] sm:tracking-[0.2em] mb-2 sm:mb-3">Follow Us</p>
              <div className="flex gap-3 sm:gap-4">
                {socialLinks.map((social, idx) => (
                  <a
                    key={idx}
                    href={social.href}
                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-gold/20 transition-all hover:scale-110 border border-white/10"
                    aria-label={social.label}
                  >
                    <social.icon size={12} className="text-gray-400 hover:text-gold transition-colors sm:text-sm" />
                  </a>
                ))}
              </div>
            </div>

            {/* Contact Hours */}
            <div className="bg-black/30 backdrop-blur-sm rounded-xl p-4 sm:p-5 border border-white/10 transition-all animate-contact-slide-right sm:col-span-2 lg:col-span-1" style={{ animationDelay: '0.2s' }}>
              <p className="text-gray-400 text-[9px] sm:text-[10px] uppercase tracking-[0.15em] sm:tracking-[0.2em] mb-2 sm:mb-3">Office Hours</p>
              <div className="space-y-1.5 sm:space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-gold"></div>
                  <span className="text-gray-300 text-xs sm:text-sm">Mon-Fri: 10:00 AM - 7:00 PM</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-gold"></div>
                  <span className="text-gray-300 text-xs sm:text-sm">Sat: 11:00 AM - 5:00 PM</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-gold/50"></div>
                  <span className="text-gray-500 text-xs sm:text-sm">Sun: Closed</span>
                </div>
              </div>
            </div>
          </div>

          {/* Map Preview Section */}
          <div className="relative overflow-hidden rounded-xl border border-white/10 group mt-6 sm:mt-8 animate-contact-fade-up">
            <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent z-10"></div>
            <img
              src="https://images.unsplash.com/photo-1569336415962-a4bd9f69cd83?w=1200&h=200&fit=crop"
              alt="Location Map Preview"
              className="w-full h-[150px] sm:h-[180px] md:h-[200px] object-cover opacity-70 group-hover:opacity-100 transition-all duration-500 scale-105 group-hover:scale-100"
            />
            <div className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 z-20">
              <div className="bg-gold/90 backdrop-blur text-navy text-[8px] sm:text-[9px] md:text-[10px] font-semibold px-2 sm:px-3 py-1 sm:py-1.5 rounded shadow-lg tracking-wide whitespace-nowrap">
                View on Map →
              </div>
            </div>
            <div className="absolute top-3 left-3 sm:top-4 sm:left-4 z-20">
              <div className="flex items-center gap-1.5 sm:gap-2 bg-black/50 backdrop-blur px-2 sm:px-3 py-1 sm:py-1.5 rounded-full">
                <FiMapPin size={10} className="text-gold sm:text-xs" />
                <span className="text-white text-[8px] sm:text-[9px] md:text-[10px] tracking-wide whitespace-nowrap">Prayagraj, Uttar Pradesh</span>
              </div>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="mt-8 sm:mt-10 pt-5 sm:pt-6 border-t border-white/10 flex flex-wrap justify-center gap-3 sm:gap-4 md:gap-6">
            {['ISO 9001:2015', 'RERA Registered', 'CRISIL Rated', 'IGBC Gold'].map((badge, idx) => (
              <div key={idx} className="flex items-center gap-1.5 sm:gap-2 text-gray-400 text-[8px] sm:text-[9px] md:text-[10px] uppercase tracking-wider">
                <FiCheckCircle size={8} className="text-gold sm:text-[10px]" />
                <span className="whitespace-nowrap">{badge}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;